import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link, ListItem, UnorderedList } from '@chakra-ui/react';

import Accordion from 'components/_core/Accordion';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { UpdateCompanyProfileTaxDetailsDocument } from 'features/Accounting/pages/Estimation/components/EstimationSettingsContainer/components/EstimationSettings/hooks/updateCompanyProfileTaxDetails/updateCompanyProfileTaxDetails.gql';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import { locales } from './locales';

const ACRE_HELP_LINK =
  'https://www.service-public.fr/particuliers/vosdroits/F11677';

enum HasAccre {
  YES = 'YES',
  NO = 'NO',
}

type SavePayload = { hasAccre: boolean };

const Accre: FC<StepComponentProps<SavePayload>> = ({
  context,
  onBack,
  onNext,
  onSave,
}) => {
  const { showToast } = useToast();
  const { companyProfile, stepProgress } = context;

  const [selectedValue, setSelectedValue] = useState(companyProfile.hasAccre);

  const [updateCompanyProfile, { loading }] = useMutation(
    UpdateCompanyProfileTaxDetailsDocument,
    {
      onCompleted: () => {
        onSave({ hasAccre: selectedValue });
        onNext();
      },
      onError: () => showToast({ message: locales.error, type: 'error' }),
      variables: {
        input: {
          companyProfileId: companyProfile.companyProfileId,
          hasAccre: selectedValue,
        },
      },
    },
  );

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body>
        <Heading marginBottom="space-32">{locales.title}</Heading>

        <Form.Radio.Group
          direction="row"
          onChange={(newValue: HasAccre) =>
            setSelectedValue(newValue === HasAccre.YES ? true : false)
          }
          radioPosition="right"
          value={selectedValue ? HasAccre.YES : HasAccre.NO}
        >
          <Form.Radio value={HasAccre.YES} variant="outline">
            <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
          </Form.Radio>

          <Form.Radio value={HasAccre.NO} variant="outline">
            <Form.Radio.Title>{locales.no}</Form.Radio.Title>
          </Form.Radio>
        </Form.Radio.Group>
      </StepLayout.Body>

      <StepLayout.Sidebar>
        <Heading marginBottom="space-16">{locales.sidebar.title}</Heading>

        <Accordion>
          <Accordion.Item title={locales.sidebar.question}>
            <Accordion.Text>{locales.sidebar.answers[1]}</Accordion.Text>

            <UnorderedList>
              {Object.values(locales.sidebar.bullets).map((bullet) => (
                <ListItem key={bullet}>
                  <Text variant="light">{bullet}</Text>
                </ListItem>
              ))}
            </UnorderedList>

            <Accordion.Text>
              {locales.sidebar.answers[2]}

              <Link fontWeight="normal" href={ACRE_HELP_LINK} isExternal>
                {locales.sidebar.answers.link}
              </Link>
            </Accordion.Text>

            <Accordion.Text>{locales.sidebar.answers[3]}</Accordion.Text>
          </Accordion.Item>
        </Accordion>
      </StepLayout.Sidebar>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isLoading={loading}
          onClick={() => updateCompanyProfile()}
        />
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default Accre;
